import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckSquareIcon } from 'src/editor/shared/icons/check-square-icon'
import { DotsVerticalIcon } from 'src/editor/shared/icons/dots-vertical-icon'
import { XCloseIcon } from 'src/editor/shared/icons/x-close-icon'
import { ActionMenu } from 'src/editor/shared/ui-lib/action-menu'
import { CommonButton, GhostIconButton } from 'src/editor/shared/ui-lib/buttons'
import {
  ButtonSizeEnum,
  ButtonTypeEnum,
} from 'src/editor/shared/ui-lib/buttons/enums'
import { CheckboxNew } from 'src/editor/shared/ui-lib/checkbox-new'
import { blogPosts } from 'src/shared/mocks/website-blog-posts.mock'
import { statusDotClasses, statusLabel } from '../consts'
import { BlogPostActionItemEnum, BlogPostFilterEnum } from '../enums'
import { filterPosts, formatDate, getPostActionItems } from '../utils'

interface PostsListingProps {
  activePostFilter: BlogPostFilterEnum
  searchValue: string
  onOpenPostSettings: (postId: string) => void
  onSchedulePost: (postId: string) => void
}

export const PostsListing = ({
  activePostFilter,
  searchValue,
  onOpenPostSettings,
  onSchedulePost,
}: PostsListingProps) => {
  const { t } = useTranslation()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const filteredPosts = filterPosts(blogPosts, activePostFilter, searchValue)

  const handleTogglePost = (id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (checked) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    setSelectedIds(
      prev => new Set([...prev, ...filteredPosts.map(post => post.id)]),
    )
  }

  const handleDeleteSelected = () => {
    // TODO: implement bulk delete API call for selectedIds
  }

  const handlePostActionSelect = (
    postId: string,
    action: BlogPostActionItemEnum,
  ) => {
    switch (action) {
      case BlogPostActionItemEnum.Settings:
        onOpenPostSettings(postId)
        break
      case BlogPostActionItemEnum.Duplicate:
        // TODO: call duplicate post API
        break
      case BlogPostActionItemEnum.Schedule:
        onSchedulePost(postId)
        break
      case BlogPostActionItemEnum.Activate:
        // TODO: call activate/publish post API
        break
      case BlogPostActionItemEnum.Delete:
        // TODO: call delete post API
        break
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-1 overflow-y-auto">
        {filteredPosts.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-M text-textTertiary">
            {t('global.no_results')}
          </div>
        ) : (
          filteredPosts.map(post => (
            <div
              key={post.id}
              className="flex items-center gap-2 rounded-md px-1 py-2 hover:bg-bgSecondary"
            >
              <CheckboxNew
                checked={selectedIds.has(post.id)}
                onChange={checked => handleTogglePost(post.id, checked)}
                className="shrink-0"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-M text-textPrimary">
                  {post.title}
                </span>
                <span className="text-S text-textTertiary">
                  {formatDate(post.date)}
                </span>
              </div>
              <span className="flex shrink-0 items-center gap-1 rounded-full bg-tagSoftGrayBg px-2 py-0.5 text-S text-textSecondary">
                <span
                  className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusDotClasses[post.status]}`}
                />
                {t(statusLabel[post.status])}
              </span>
              <ActionMenu
                items={getPostActionItems(post.status)}
                onSelect={action =>
                  handlePostActionSelect(
                    post.id,
                    action as BlogPostActionItemEnum,
                  )
                }
                triggerElement={setIsOpened => (
                  <GhostIconButton
                    onClick={() => setIsOpened(true)}
                    icon={<DotsVerticalIcon className="fill-iconSecondary" />}
                  />
                )}
              />
            </div>
          ))
        )}
      </div>
      {selectedIds.size > 0 && (
        <div className="flex shrink-0 gap-2 border-border200 border-t pt-3">
          <CommonButton
            type={ButtonTypeEnum.Ghost}
            size={ButtonSizeEnum.Medium}
            className="flex-1"
            iconBefore={<CheckSquareIcon />}
            onClick={handleSelectAll}
          >
            {t('website_settings.sidebar.blog.categories.select_all')}
          </CommonButton>
          <CommonButton
            type={ButtonTypeEnum.Ghost}
            size={ButtonSizeEnum.Medium}
            className="flex-1 text-error-500 [&>svg>path]:stroke-iconError [&>svg]:text-iconError"
            iconBefore={<XCloseIcon fill="currentColor" className="h-5 w-5" />}
            onClick={handleDeleteSelected}
          >
            {t('website_settings.sidebar.blog.categories.delete_selected')}
          </CommonButton>
        </div>
      )}
    </div>
  )
}
